import { useMemo, useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { EmptyState } from "@/components/common/EmptyState";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { SelectField } from "@/components/common/SelectField";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { NAFA_FORM_WIDTH } from "@/components/ui/page-container";
import { useProducts } from "@/features/products/hooks/useProducts";
import { useProvisions } from "@/features/provisions/hooks/useProvisions";
import {
  emptyIngredientLine,
  filledIngredients,
  IngredientLines,
  type IngredientLine,
} from "@/features/recipes/components/IngredientLines";
import { useCreateRecipe, useRecipes, useUpdateRecipe } from "@/features/recipes/hooks/useRecipes";
import type { Recipe } from "@/features/recipes/types";
import { getApiValidationErrors } from "@/lib/apiClient";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";

type RecipeFieldErrors = {
  productId?: string;
  name?: string;
};

const RECIPE_FIELD_IDS = {
  productId: "recipe-product",
  name: "recipe-name",
  description: "recipe-description",
} as const;

const linesOf = (recipe?: Recipe): IngredientLine[] => {
  const ingredients = recipe?.ingredients ?? [];
  if (ingredients.length === 0) return [emptyIngredientLine()];
  return ingredients.map((ingredient) => ({
    key: crypto.randomUUID(),
    provisionId: ingredient.provisionId,
    quantity: String(ingredient.quantity),
  }));
};

export default function RecipeFormPage({ recipe, onBack }: { recipe?: Recipe; onBack: () => void }) {
  const isEditing = Boolean(recipe);

  const [productId, setProductId] = useState(recipe?.productId ?? "");
  const [name, setName] = useState(recipe?.name ?? "");
  const [description, setDescription] = useState(recipe?.description ?? "");
  const [lines, setLines] = useState<IngredientLine[]>(() => linesOf(recipe));
  const [fieldErrors, setFieldErrors] = useState<RecipeFieldErrors>({});
  const [formError, setFormError] = useState<string>();
  const toast = useToast();

  const { products, isLoading: loadingProducts } = useProducts();
  const { provisions } = useProvisions();
  const { recipes } = useRecipes();
  const { create, isCreating } = useCreateRecipe();
  const { update, isUpdating } = useUpdateRecipe();

  const isSaving = isCreating || isUpdating;

  const clearFieldError = (field: keyof RecipeFieldErrors) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError(undefined);
  };

  /**
   * The API allows one recipe per product and answers 409 for the rest. The
   * product list never populates its own recipe field, so coverage is read
   * from the recipes, which do carry productId.
   */
  const covered = useMemo(() => new Set(recipes.map((item) => item.productId).filter(Boolean)), [recipes]);
  const available = useMemo(() => products.filter((product) => !covered.has(product.id)), [products, covered]);

  const filled = useMemo(() => filledIngredients(lines), [lines]);

  const producedName = useMemo(
    () => products.find((product) => product.id === recipe?.productId)?.name,
    [products, recipe],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const errors: RecipeFieldErrors = {};
    if (!isEditing && !productId) errors.productId = "Choisissez le produit fabriqué.";
    if (!name.trim()) errors.name = "Donnez un nom à la recette.";
    setFieldErrors(errors);
    setFormError(
      Object.keys(errors).length > 0
        ? "Corrigez les champs signalés avant d’enregistrer."
        : filled.length === 0
          ? "Ajoutez au moins une matière première avec sa quantité."
          : undefined,
    );
    focusFirstField(Object.keys(errors).map((field) => RECIPE_FIELD_IDS[field as keyof typeof RECIPE_FIELD_IDS]));

    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0] ?? "Corrigez les champs signalés.");
      return;
    }
    if (filled.length === 0) {
      toast.error("Ajoutez au moins une matière première avec sa quantité.");
      return;
    }

    try {
      if (recipe) {
        await update(recipe.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          ingredients: filled,
        });
        toast.success(`Recette « ${name.trim()} » mise à jour.`);
      } else {
        await create({
          name: name.trim(),
          description: description.trim() || undefined,
          productId,
          ingredients: filled,
        });
        toast.success(`Recette « ${name.trim()} » créée.`);
      }
      onBack();
    } catch (e) {
      // The lines stay put so nothing has to be retyped.
      const apiErrors = getApiValidationErrors(e);
      const nextErrors: RecipeFieldErrors = {
        productId: apiErrors.productId,
        name: apiErrors.name,
      };
      const fieldErrorIds = Object.entries(nextErrors)
        .filter(([, message]) => Boolean(message))
        .map(([field]) => RECIPE_FIELD_IDS[field as keyof typeof RECIPE_FIELD_IDS]);
      setFieldErrors(nextErrors);
      setFormError(
        fieldErrorIds.length > 0
          ? "Corrigez les champs signalés avant d’enregistrer."
          : (toErrorMessage(e) ?? undefined),
      );
      focusFirstField(fieldErrorIds);
      toast.error(toErrorMessage(e) ?? "La recette n’a pas pu être enregistrée.");
    }
  };

  const noProductLeft = !isEditing && !loadingProducts && available.length === 0;

  return (
    <form onSubmit={handleSubmit} className={`flex ${NAFA_FORM_WIDTH} flex-col gap-6`}>
      {isSaving && <LoadingOverlay message="Enregistrement…" />}

      <div className="flex items-start gap-3">
        <BackButton onClick={onBack} />
        <PageHeader
          title={isEditing ? "Modifier la recette" : "Nouvelle recette"}
          subtitle="Ce qu’il faut consommer pour fabriquer un produit."
        />
      </div>

      <FormErrorSummary
        fields={[
          { id: RECIPE_FIELD_IDS.productId, label: "Produit fabriqué", message: fieldErrors.productId },
          { id: RECIPE_FIELD_IDS.name, label: "Nom de la recette", message: fieldErrors.name },
        ]}
      >
        {formError}
      </FormErrorSummary>

      {noProductLeft ? (
        <EmptyState
          title="Aucun produit disponible"
          hint={
            products.length > 0 ? "Tous vos produits ont déjà une recette." : "Créez d’abord un produit à fabriquer."
          }
        />
      ) : (
        <>
          <FormField
            label="Produit fabriqué"
            hint={isEditing ? "Le produit fabriqué ne peut pas changer." : undefined}
            error={fieldErrors.productId}
            htmlFor={RECIPE_FIELD_IDS.productId}
            errorId={`${RECIPE_FIELD_IDS.productId}-error`}
            hintId={`${RECIPE_FIELD_IDS.productId}-hint`}
          >
            {isEditing ? (
              <Input
                id={RECIPE_FIELD_IDS.productId}
                readOnly
                value={producedName ?? "Produit inconnu"}
                className="bg-muted/40 text-muted-foreground"
                aria-describedby={
                  fieldErrors.productId
                    ? `${RECIPE_FIELD_IDS.productId}-error ${RECIPE_FIELD_IDS.productId}-hint`
                    : `${RECIPE_FIELD_IDS.productId}-hint`
                }
                aria-invalid={Boolean(fieldErrors.productId)}
              />
            ) : (
              <SelectField
                value={productId}
                onChange={(value) => {
                  setProductId(value);
                  clearFieldError("productId");
                }}
                id={RECIPE_FIELD_IDS.productId}
                placeholder="Choisir un produit…"
                aria-invalid={Boolean(fieldErrors.productId)}
                aria-describedby={fieldErrors.productId ? `${RECIPE_FIELD_IDS.productId}-error` : undefined}
                options={available.map((product) => ({
                  value: product.id,
                  label: product.name,
                }))}
              />
            )}
          </FormField>

          <FormField
            label="Nom de la recette"
            error={fieldErrors.name}
            htmlFor={RECIPE_FIELD_IDS.name}
            errorId={`${RECIPE_FIELD_IDS.name}-error`}
          >
            <Input
              id={RECIPE_FIELD_IDS.name}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError("name");
              }}
              placeholder="ex. Poulet yassa — portion"
              aria-invalid={Boolean(fieldErrors.name)}
              aria-describedby={fieldErrors.name ? `${RECIPE_FIELD_IDS.name}-error` : undefined}
            />
          </FormField>

          <FormField
            label="Description"
            htmlFor={RECIPE_FIELD_IDS.description}
            hint="Facultatif."
            hintId={`${RECIPE_FIELD_IDS.description}-hint`}
          >
            <Input
              id={RECIPE_FIELD_IDS.description}
              aria-describedby={`${RECIPE_FIELD_IDS.description}-hint`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Précision utile pour vos équipes"
            />
          </FormField>

          <div className="flex flex-col gap-2">
            <div>
              <p className="text-[15px] font-medium">Matières premières consommées</p>
              <p className="text-sm text-muted-foreground">
                Les quantités nécessaires pour fabriquer une fois ce produit.
              </p>
            </div>

            <IngredientLines lines={lines} onChange={setLines} provisions={provisions} />
          </div>

          <div className="flex gap-3">
            <Button type="submit" size="lg" disabled={isSaving}>
              {isEditing ? "Enregistrer" : "Créer la recette"}
            </Button>
            <Button type="button" variant="outline" size="lg" onClick={onBack}>
              Annuler
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
